import {
  Delete,
  ForwardToInbox,
  NoAccounts,
  SettingsBackupRestore,
  SupervisorAccount,
} from '@mui/icons-material';
import React, { useState } from 'react';
import { useNotification } from '../../hooks/NotificationContext';
import {
  modifyUserAccountStatus,
  resendInvites,
  updateUserRole,
} from '../../modules/users';
import ConfirmationModal from '../elements/ConfirmationModal';
import TableActionsMenu from '../elements/TableActionsMenu';
import { InputLabel, MenuItem, Select } from '@mui/material';
import { roleOptions } from '../../static/common';

const UserActions = ({
  row,
  fetchCustomerList,
  currentUser,
  orgManagersCount = 0,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const { addNotification } = useNotification();
  const [selectedRole, setSelectedRole] = useState(row?.role || '');

  const openConfirm = (action) => {
    setConfirmAction(action);
    if (action === 'role') setSelectedRole(row?.role);
    setShowConfirm(true);
  };

  const closeConfirm = () => {
    setShowConfirm(false);
    setConfirmAction(null);
  };

  const handleSubmit = async () => {
    if (!confirmAction || !row?.id) return;

    setLoading(true);
    try {
      if (confirmAction === 'invite') {
        await resendInvites(row);
        addNotification('success', 'Invite sent successfully!');
      } else if (confirmAction === 'role') {
        if (!selectedRole || selectedRole === row.role) {
          addNotification('info', 'No changes made to the user role.');
        } else {
          await updateUserRole(selectedRole, row.id);
          addNotification('success', 'User role updated successfully!');
          fetchCustomerList();
        }
      } else {
        await modifyUserAccountStatus([row.id], confirmAction);
        const statusMsg = {
          delete: 'deleted permanently',
          deactivate: 'deactivated',
          restore: 'reactivated',
        };
        addNotification(
          'success',
          `User ${statusMsg[confirmAction]} successfully!`
        );
        fetchCustomerList();
      }
      closeConfirm();
    } catch (err) {
      addNotification('danger', err.message || 'Something went wrong.');
    }
    setLoading(false);
  };

  const isInviteDisabled = row?.status === 'invite-accepted';

  const confirmationTextMap = {
    invite: `Are you sure you want to resend the invite to ${row?.first_name}?`,
    role:
      row?.role === 'team-member'
        ? 'Are you sure you want to promote this user to organization manager?'
        : 'Are you sure you want to demote this user back to team member?',
    delete: `Are you sure you want to permanently delete ${row?.first_name}? This action cannot be undone.`,
    deactivate: `Are you sure you want to deactivate ${row?.first_name}?`,
    restore: `Are you sure you want to reactivate ${row?.first_name}?`,
  };

  const menuOptions = [];

  if (row?.status === 'active') {
    menuOptions.push({
      label: 'Update Role',
      icon: <SupervisorAccount fontSize="small" />,
      onClick: () => openConfirm('role'),
    });
  }

  if (row?.status === 'invited') {
    menuOptions.push({
      label: 'Resend Invite',
      icon: <ForwardToInbox fontSize="small" />,
      onClick: () => openConfirm('invite'),
      disabled: isInviteDisabled,
    });
  }
  if (currentUser?.role === 'org-admin' && row?.role !== 'org-admin') {
    if (row?.status !== 'active') {
      menuOptions.push({
        label: 'Delete Permanently',
        icon: <Delete fontSize="small" />,
        onClick: () => openConfirm('delete'),
      });
    } else {
      menuOptions.push({
        label: 'Deactivate User',
        icon: <NoAccounts fontSize="small" />,
        onClick: () => openConfirm('deactivate'),
      });
    }

    if (row?.status === 'inactive') {
      menuOptions.push({
        label: 'Reactivate User',
        icon: <SettingsBackupRestore fontSize="small" />,
        onClick: () => openConfirm('restore'),
      });
    }
  }

  const modalTitleMap = {
    invite: 'Resend Invite',
    role: 'Update Member Role',
    delete: 'Delete User',
    deactivate: 'Deactivate User',
    restore: 'Reactivate User',
  };

  return (
    <>
      {menuOptions?.length > 0 && <TableActionsMenu options={menuOptions} />}
      <ConfirmationModal
        show={showConfirm}
        handleClose={closeConfirm}
        handleConfirm={handleSubmit}
        title={modalTitleMap[confirmAction] || 'Confirm Action'}
        body={
          confirmAction === 'role' ? (
            <>
              <InputLabel id="select-role-label" sx={{ mb: 1 }}>
                Select New Role
              </InputLabel>
              <Select
                labelId="select-role-label"
                fullWidth
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}>
                {roleOptions?.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </>
          ) : (
            confirmationTextMap[confirmAction]
          )
        }
        buttonLabel={loading ? 'Please wait...' : 'Confirm'}
        buttonLabelSecondary="Cancel"
        buttonStyle="primary"
        loading={loading}
      />
    </>
  );
};

export default UserActions;
